import { useEffect, useState } from 'react';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { TaxCode } from '../../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../../clients/quipli-service/tax/tax-api-v1';
import { toast } from '../../../../util/toasts';

export const useOrderContractConfigurations = () => {
  const { getMultipleConfigurations } = useAPI();
  const { searchTaxCodes } = useTaxApiV1();

  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [showRates, setShowRates] = useState<boolean>(false);
  const [taxCodes, setTaxCodes] = useState<TaxCode[] | undefined>(undefined);
  const [taxLabel, setTaxLabel] = useState<string>('Tax');
  const [lockSecurityDeposits, setLockSecurityDeposits] = useState<boolean>(false);
  const [isQrCodeEnabled, setIsQrCodeEnabled] = useState<boolean>(false);
  const [isCustomSecurityDepositEnabled, setIsCustomSecurityDepositEnabled] = useState<boolean>(false);
  const [isCustomOrderRentalRatesEnabled, setIsCustomOrderRentalRatesEnabled] = useState<boolean>(false);
  const [onlyShowStoreHoursOnDateTimePicker, setOnlyShowStoreHoursOnDateTimePicker] = useState<boolean>(false);
  const [showPricesWithTax, setShowPricesWithTax] = useState<boolean>(false);

  useEffect(() => {
    getMultipleConfigurations([
      'CONF_ENABLE_QR_CODE',
      'CONF_ORDER_PERCENTAGE_SECURITY',
      'CONF_CUSTOM_ORDER_RENTAL_RATES',
      'CONF_SITE_TAX_OPTIONS',
      'CONF_PRODUCT_IMAGES_V2',
      'CONF_PRORATE_PARTIAL_CYCLE_BILLING_INVOICES',
      'CONF_SALE_SERIAL_TRACKING',
      'CONF_LOCK_SECURITY_DEPOSITS',
      'CONF_USE_STORE_HOURS_DATE_PICK',
      'CONF_SHOW_PRICES_WITH_TAX',
      'CONF_PRICE_TAX_LABEL',
    ])
      .then((response) => {
        setConfigurations(response);
        if (response.find((c) => c.variable === 'CONF_PRORATE_PARTIAL_CYCLE_BILLING_INVOICES')?.value === '1') {
          setShowRates(false);
        }
        if (response.find((c) => c.variable === 'CONF_SITE_TAX_OPTIONS')?.value === '5') {
          searchTaxCodes({ pageSize: 500 }).then((taxCodes) => setTaxCodes(taxCodes.items));
        }

        if (response.find((c) => c.variable === 'CONF_PRICE_TAX_LABEL')?.value) {
          setTaxLabel(response.find((c) => c.variable === 'CONF_PRICE_TAX_LABEL')!.value);
        }

        setLockSecurityDeposits(response.find((c) => c.variable === 'CONF_LOCK_SECURITY_DEPOSITS')?.value === '1');

        setIsQrCodeEnabled(response.find((c) => c.variable === 'CONF_ENABLE_QR_CODE')?.value === '1');
        setIsCustomSecurityDepositEnabled(
          response.find((c) => c.variable === 'CONF_ORDER_PERCENTAGE_SECURITY')?.value === '1',
        );
        setIsCustomOrderRentalRatesEnabled(
          response.find((c) => c.variable === 'CONF_CUSTOM_ORDER_RENTAL_RATES')?.value === '1',
        );
        setOnlyShowStoreHoursOnDateTimePicker(
          response.find((c) => c.variable === 'CONF_USE_STORE_HOURS_DATE_PICK')?.value === '1',
        );
        setShowPricesWithTax(response.find((c) => c.variable === 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1');
      })
      .catch(() => {
        toast('Failed to load configurations', 'error');
        setConfigurations([]);
      });
  }, []);

  return {
    configurations,
    showRates,
    setShowRates,
    taxCodes,
    taxLabel,
    lockSecurityDeposits,
    isQrCodeEnabled,
    isCustomSecurityDepositEnabled,
    isCustomOrderRentalRatesEnabled,
    onlyShowStoreHoursOnDateTimePicker,
    showPricesWithTax,
  };
};
